import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Heart, Check, User, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const JOURNEY_LABELS = {
  trying: '🌷 En essai',
  pregnant: '🤰 Enceinte',
  postpartum: '🍼 Post-partum',
  mom: '👶 Maman',
  mom_older: '🌻 Enfants + grands',
  protocole: '🩺 En protocole',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / 86400);
  if (days < 7) return `il y a ${days}j`;
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

/**
 * 🌸 Élodie #1096 — Widget "Nouvelles mamans à accueillir"
 * Affiche les 10 dernières inscrites + bouton "Souhaiter la bienvenue"
 */
const NewMembersWidget = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [welcomingId, setWelcomingId] = useState(null);
  const [myWelcomes, setMyWelcomes] = useState(0);

  const fetchMembers = useCallback(async () => {
    if (!token) return;
    try {
      const [r, c] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/community/new-members?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/community/my-welcomes-count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setMembers(r.data?.members || []);
      setMyWelcomes(c.data?.count || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const handleWelcome = async (memberId, firstName) => {
    if (welcomingId) return;
    setWelcomingId(memberId);
    try {
      await axios.post(`${BACKEND_URL}/api/community/welcome/${memberId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Bienvenue envoyée à ${firstName} 💕`);
      // Update local state
      setMembers(prev => prev.map(m => m.id === memberId ? { ...m, welcomed_by_me: true } : m));
      setMyWelcomes(w => w + 1);
    } catch (e) {
      const msg = e.response?.data?.detail || "Impossible d'envoyer 💕";
      toast.error(msg);
    } finally { setWelcomingId(null); }
  };

  if (loading || members.length === 0) return null;

  return (
    <div className="rounded-2xl bg-gradient-to-br from-[#FFF5F3] to-[#FCE4EC]/60 shadow-sm border border-white/60 p-4 mb-4" data-testid="new-members-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-black uppercase tracking-widest text-[#5C3D36] flex items-center gap-1.5">
            🌸 Nouvelles mamans
          </h3>
          <p className="text-[11px] text-[#9D8189] mt-0.5">
            Elles viennent d'arriver — souhaite-leur la bienvenue 💕
          </p>
        </div>
        {myWelcomes > 0 && (
          <div className="text-right">
            <p className="text-[9px] uppercase tracking-widest text-[#9D8189] font-bold">Accueillies</p>
            <p className="text-lg font-black text-[#E8A5C4]" data-testid="my-welcomes-count">{myWelcomes} 💕</p>
          </div>
        )}
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
        {members.map((m) => (
          <div
            key={m.id}
            className="flex-shrink-0 w-32 rounded-2xl bg-white shadow-sm p-3 flex flex-col items-center gap-1.5 snap-start"
            data-testid={`new-member-${m.id}`}
          >
            <button
              onClick={() => navigate(`/dashboard/profile/${m.id}`)}
              className="relative"
              aria-label={`Voir profil de ${m.first_name}`}
            >
              {m.profile_photo_url ? (
                <img
                  src={m.profile_photo_url}
                  alt={m.first_name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-[#E8A5C4]/30"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#FFD5C8] to-[#E8A5C4] flex items-center justify-center">
                  <User className="w-6 h-6 text-white" strokeWidth={2.2} />
                </div>
              )}
            </button>
            <p className="text-xs font-bold text-[#5C3D36] truncate w-full text-center">
              {m.pseudo || m.first_name}
            </p>
            {m.journey_stage && JOURNEY_LABELS[m.journey_stage] && (
              <p className="text-[9px] text-[#9D8189] truncate w-full text-center">
                {JOURNEY_LABELS[m.journey_stage]}
              </p>
            )}
            <p className="text-[9px] text-[#B8A5AB]">{timeAgo(m.created_at)}</p>
            {m.welcomed_by_me ? (
              <div className="w-full mt-1 py-1 rounded-full bg-green-100 text-green-700 text-[10px] font-bold flex items-center justify-center gap-1">
                <Check className="w-3 h-3" strokeWidth={3} /> Accueillie
              </div>
            ) : (
              <button
                onClick={() => handleWelcome(m.id, m.first_name)}
                disabled={welcomingId === m.id}
                className="w-full mt-1 py-1 rounded-full bg-gradient-to-r from-[#E8A5C4] to-[#D48CB0] hover:shadow-md text-white text-[10px] font-bold flex items-center justify-center gap-1 disabled:opacity-60 transition-all"
                data-testid={`welcome-btn-${m.id}`}
              >
                <Heart className="w-3 h-3" strokeWidth={2.5} />
                {welcomingId === m.id ? 'Envoi…' : 'Bienvenue'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewMembersWidget;
